import React, { useEffect, useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import { useNavigate } from "react-router-dom";
import Color from "../../modules/Color";
import Brand from "../../api/Brand";
import { useSnackbar } from "../../contexts/SnackbarContext";
import OnboardingShell from "../OnboardingShell";
import { getEnvironment } from "../../utils/getEnvironment";
import { useCookies } from "react-cookie";
import { APP_MAX_WIDTH } from "../../constants/layout";
import { FONTS, GLASS_SX } from "../../constants/styles";
import ColoredIcon from "../ColoredIcon";
import DefaultIcon from "../../img/default_icon.svg";

const ANIMATION_DELAY = 2000;
const FAST_FORWARD_DELAY = 400;
const environment = getEnvironment();

const animationSteps = [
    "Reserving your app name",
    "Designing the perfect color palette",
    "Creating your branded wallpaper",
    "Learning about your business",
    "Adding your basic services",
    "Writing service descriptions",
    "Setting competitive prices",
    "Configuring your schedule",
    "Pinning your location on the map",
    "Assembling your mobile app",
    "Generating your booking link",
    "Finishing up\u2026",
];

const TOTAL_STEPS = animationSteps.length;

// SVG circular progress ring
const ProgressRing = ({
    progress,
    size = 160,
    strokeWidth = 4,
}: {
    progress: number;
    size?: number;
    strokeWidth?: number;
}) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - progress);

    return (
        <svg
            width={size}
            height={size}
            style={{
                position: "absolute",
                top: 0,
                left: 0,
                transform: "rotate(-90deg)",
            }}
        >
            {/* Background circle */}
            <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke="rgba(255,255,255,0.15)"
                strokeWidth={strokeWidth}
            />
            {/* Progress circle */}
            <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke="rgba(255,255,255,0.8)"
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                strokeLinecap="round"
                style={{ transition: "stroke-dashoffset 0.6s ease" }}
            />
        </svg>
    );
};

const NewAnimatedCreation = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();

    const [currentStep, setCurrentStep] = useState(0);
    const [brandCreated, setBrandCreated] = useState(false);
    const [clientPreviewURL, setClientPreviewURL] = useState("");

    const [brandName, setBrandName] = useState<string>("Your App");
    const [brandLogo, setBrandLogo] = useState<string | undefined>();

    const [{ refBrandAddress }, , removeCookie] = useCookies([
        "refBrandAddress",
    ]);

    useEffect(() => {
        try {
            const raw = sessionStorage.getItem("brand");
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed?.name) setBrandName(parsed.name);
                if (parsed?.logo) {
                    // Verify the logo URL actually loads (may be stale Cloudinary 404)
                    const img = new Image();
                    img.onload = () => setBrandLogo(parsed.logo);
                    img.onerror = () => setBrandLogo(undefined);
                    img.src = parsed.logo;
                }
            }
        } catch (e) {
            console.error("Failed to parse brand info:", e);
        }
    }, []);

    const didRunRef = useRef(false);

    useEffect(() => {
        if (didRunRef.current) return;
        didRunRef.current = true;

        const alreadyCreated =
            sessionStorage.getItem("brandCreated") === "true";

        if (alreadyCreated) {
            const brandRaw = JSON.parse(
                sessionStorage.getItem("brand") || "{}"
            );

            if (brandRaw?.name) setBrandName(brandRaw.name);
            if (brandRaw?.logo) setBrandLogo(brandRaw.logo);

            const address = brandRaw.address;
            const base = environment.preview_address;
            const url = new URL(base);

            const hostParts = url.hostname.split(".");
            const isLocalhost = url.hostname === "localhost";

            const newHost = isLocalhost
                ? `${address}.localhost`
                : `${address}.${hostParts.join(".")}`;

            url.hostname = newHost;

            setClientPreviewURL(url.toString() + "?preview=true");
            setCurrentStep(animationSteps.length);
            setBrandCreated(true);
            return;
        }

        const run = async () => {
            try {
                const brandRaw = JSON.parse(
                    sessionStorage.getItem("brand") || "{}"
                );

                if (!brandRaw.name || !brandRaw.address || !brandRaw.business)
                    throw new Error("Incomplete brand info");

                const base = environment.preview_address;
                const url = new URL(base);

                const hostParts = url.hostname.split(".");
                const isLocalhost = url.hostname === "localhost";

                const selectedColor = Color.fromJSON(brandRaw.color);

                const result = await Brand.registerNew(
                    brandRaw.name,
                    brandRaw.address,
                    selectedColor,
                    brandRaw.business,
                    brandRaw.logo,
                    refBrandAddress
                );

                const address = result.slug;

                const newHost = isLocalhost
                    ? `${address}.localhost`
                    : `${address}.${hostParts.join(".")}`;

                url.hostname = newHost;

                const previewUrl = url.toString() + "?preview=true";

                setClientPreviewURL(previewUrl);
                sessionStorage.setItem("clientPreviewUrl", previewUrl);
                setBrandCreated(true);

                sessionStorage.setItem("brandCreated", "true");
                sessionStorage.setItem(
                    "brand",
                    JSON.stringify({
                        ...brandRaw,
                        brandId: result.brand,
                        locationId: result.location,
                        address: result.slug,
                    })
                );
            } catch (err) {
                console.error("Brand creation failed", err);
                showSnackbar(
                    "Something went wrong during creation. Please try again."
                );
                navigate("/creation/brand");
            }
        };

        run();
    }, [navigate, refBrandAddress, showSnackbar]);

    // Step wheel animation — hold at last step until brand is created
    // Fast-forward remaining steps when API completes early
    useEffect(() => {
        const lastIndex = animationSteps.length - 1;

        if (currentStep < lastIndex) {
            // Use fast delay if brand is already created (API finished early)
            const delay = brandCreated ? FAST_FORWARD_DELAY : ANIMATION_DELAY;
            const timer = setTimeout(
                () => setCurrentStep((prev) => prev + 1),
                delay
            );
            return () => clearTimeout(timer);
        } else if (currentStep === lastIndex && brandCreated) {
            // Brand is ready — advance past last step to trigger finished
            const timer = setTimeout(
                () => setCurrentStep((prev) => prev + 1),
                600
            );
            return () => clearTimeout(timer);
        }
        // At last step but brand not created yet → hold (dots stay, text stays)
    }, [currentStep, brandCreated]);

    const isFinished = currentStep >= animationSteps.length && brandCreated;

    // Remove ref cookie as soon as we're finished
    useEffect(() => {
        if (isFinished) {
            removeCookie("refBrandAddress");
        }
    }, [isFinished, removeCookie]);

    const goToClientApp = () => {
        const url =
            clientPreviewURL ||
            sessionStorage.getItem("clientPreviewURL") ||
            "";
        if (!url) return;
        setTimeout(() => {
            window.location.href = url;
        }, 100);
    };

    const goToAdminInfo = () => {
        navigate("/creation/admin-info");
    };

    const progress = Math.min(currentStep / TOTAL_STEPS, 1);
    const currentStepText =
        animationSteps[Math.min(currentStep, animationSteps.length - 1)];
    const RING_SIZE = 160;
    const ICON_SIZE = 112;

    return (
        <OnboardingShell variant="celebration" showLogo>
            <Stack
                flex={1}
                width="100%"
                alignItems="center"
                justifyContent="center"
                sx={{
                    textAlign: "center",
                    px: 2,
                    boxSizing: "border-box",
                    pb: "calc(env(safe-area-inset-bottom) + 32px)",
                }}
            >
                {/* Both states rendered; toggled via display to avoid Weglot DOM crash */}

                {/* ─── LOADING STATE ─── */}
                <Stack
                    spacing={4}
                    alignItems="center"
                    sx={{
                        display: isFinished ? "none" : "flex",
                        "@keyframes fadeUp": {
                            from: {
                                opacity: 0,
                                transform: "translateY(20px)",
                            },
                            to: {
                                opacity: 1,
                                transform: "translateY(0)",
                            },
                        },
                        animation: "fadeUp 0.5s ease-out",
                    }}
                >
                    {/* Brand icon with progress ring */}
                    <Box
                        sx={{
                            position: "relative",
                            width: RING_SIZE,
                            height: RING_SIZE,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            "@keyframes pulseGlow": {
                                "0%, 100%": {
                                    boxShadow:
                                        "0 0 30px rgba(255,255,255,0.15)",
                                },
                                "50%": {
                                    boxShadow:
                                        "0 0 50px rgba(255,255,255,0.3)",
                                },
                            },
                            borderRadius: "50%",
                            animation: "pulseGlow 2s ease-in-out infinite",
                        }}
                    >
                        <ProgressRing
                            progress={progress}
                            size={RING_SIZE}
                        />
                        <Box
                            sx={{
                                width: ICON_SIZE,
                                height: ICON_SIZE,
                                borderRadius: "26px",
                                bgcolor: "rgba(255,255,255,0.2)",
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                                zIndex: 1,
                            }}
                        >
                            {brandLogo ? (
                                <Box
                                    width="100%"
                                    height="100%"
                                    sx={{
                                        backgroundImage: `url(${brandLogo})`,
                                        backgroundPosition: "center",
                                        backgroundRepeat: "no-repeat",
                                        backgroundSize: "cover",
                                    }}
                                />
                            ) : (
                                <ColoredIcon
                                    icon={DefaultIcon}
                                    size={72}
                                    color="white"
                                />
                            )}
                        </Box>
                    </Box>

                    {/* Step counter */}
                    <Stack spacing={1} alignItems="center">
                        <Typography
                            sx={{
                                fontFamily: FONTS.display,
                                fontWeight: 700,
                                fontSize: "24px",
                                color: "white",
                            }}
                        >
                            Building {brandName}
                        </Typography>
                        <Typography
                            key={currentStep}
                            sx={{
                                fontFamily: FONTS.body,
                                fontSize: "14px",
                                color: "rgba(255,255,255,0.75)",
                                minHeight: "20px",
                                "@keyframes stepFade": {
                                    from: {
                                        opacity: 0,
                                        transform: "translateY(6px)",
                                    },
                                    to: {
                                        opacity: 1,
                                        transform: "translateY(0)",
                                    },
                                },
                                animation: "stepFade 0.4s ease-out",
                            }}
                        >
                            {currentStepText}
                        </Typography>
                    </Stack>

                    {/* Step dots */}
                    <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="center"
                    >
                        {animationSteps.map((_, i) => (
                            <Box
                                key={i}
                                sx={{
                                    width: i < currentStep ? 16 : 6,
                                    height: 6,
                                    borderRadius: "3px",
                                    bgcolor:
                                        i < currentStep
                                            ? "rgba(255,255,255,0.8)"
                                            : "rgba(255,255,255,0.2)",
                                    transition:
                                        "width 0.3s ease, background-color 0.3s ease",
                                }}
                            />
                        ))}
                    </Stack>
                </Stack>

                {/* ─── FINISHED STATE ─── */}
                <Stack
                    spacing={3}
                    alignItems="center"
                    width="100%"
                    sx={{
                        display: isFinished ? "flex" : "none",
                        "@keyframes revealScale": {
                            "0%": {
                                opacity: 0,
                                transform: "scale(0.85)",
                            },
                            "100%": {
                                opacity: 1,
                                transform: "scale(1)",
                            },
                        },
                        animation: isFinished
                            ? "revealScale 0.5s ease-out"
                            : "none",
                    }}
                >
                    {/* Brand icon with celebratory glow ring */}
                    <Box
                        sx={{
                            position: "relative",
                            "@keyframes celebrateIn": {
                                "0%": {
                                    opacity: 0,
                                    transform: "scale(0.6)",
                                },
                                "50%": {
                                    transform: "scale(1.05)",
                                },
                                "100%": {
                                    opacity: 1,
                                    transform: "scale(1)",
                                },
                            },
                            animation: isFinished
                                ? "celebrateIn 0.6s ease-out"
                                : "none",
                        }}
                    >
                        {/* Outer glow ring */}
                        <Box
                            sx={{
                                position: "absolute",
                                top: -16,
                                left: -16,
                                right: -16,
                                bottom: -16,
                                borderRadius: "38px",
                                border: "2px solid rgba(255,255,255,0.25)",
                                "@keyframes glowPulse": {
                                    "0%, 100%": {
                                        boxShadow:
                                            "0 0 20px rgba(255,255,255,0.1), inset 0 0 20px rgba(255,255,255,0.05)",
                                        opacity: 0.8,
                                    },
                                    "50%": {
                                        boxShadow:
                                            "0 0 40px rgba(255,255,255,0.2), inset 0 0 40px rgba(255,255,255,0.1)",
                                        opacity: 1,
                                    },
                                },
                                animation:
                                    "glowPulse 2.5s ease-in-out infinite",
                            }}
                        />
                        {/* Sparkle dots */}
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                            <Box
                                key={i}
                                sx={{
                                    position: "absolute",
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    bgcolor: "rgba(255,255,255,0.7)",
                                    top: "50%",
                                    left: "50%",
                                    "@keyframes sparkle": {
                                        "0%": {
                                            transform: `rotate(${i * 60}deg) translateX(60px) scale(0)`,
                                            opacity: 0,
                                        },
                                        "40%": {
                                            transform: `rotate(${i * 60}deg) translateX(80px) scale(1)`,
                                            opacity: 1,
                                        },
                                        "100%": {
                                            transform: `rotate(${i * 60 + 20}deg) translateX(100px) scale(0)`,
                                            opacity: 0,
                                        },
                                    },
                                    animation: isFinished
                                        ? `sparkle 1.2s ease-out ${200 + i * 100}ms both`
                                        : "none",
                                }}
                            />
                        ))}
                        <Box
                            sx={{
                                width: 120,
                                height: 120,
                                borderRadius: "30px",
                                bgcolor: "rgba(255,255,255,0.2)",
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxShadow:
                                    "0 8px 40px rgba(0,0,0,0.15), 0 0 60px rgba(255,255,255,0.1)",
                                position: "relative",
                                zIndex: 1,
                            }}
                        >
                            {brandLogo ? (
                                <Box
                                    width="100%"
                                    height="100%"
                                    sx={{
                                        backgroundImage: `url(${brandLogo})`,
                                        backgroundPosition: "center",
                                        backgroundRepeat: "no-repeat",
                                        backgroundSize: "cover",
                                    }}
                                />
                            ) : (
                                <ColoredIcon
                                    icon={DefaultIcon}
                                    size={80}
                                    color="white"
                                />
                            )}
                        </Box>
                    </Box>

                    <Stack spacing={0.5} alignItems="center">
                        <Typography
                            className="no-translation"
                            sx={{
                                fontFamily: FONTS.display,
                                fontWeight: 700,
                                fontSize: "28px",
                                color: "white",
                            }}
                        >
                            {brandName}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONTS.body,
                                fontSize: "15px",
                                color: "rgba(255,255,255,0.8)",
                            }}
                        >
                            Your apps are ready
                        </Typography>
                    </Stack>

                    {/* Action cards */}
                    <Stack
                        spacing={1.5}
                        alignItems="center"
                        width="100%"
                        sx={{ mt: 1 }}
                    >
                        <ActionCard
                            icon={
                                <PhoneIphoneRoundedIcon
                                    sx={{ color: "white", fontSize: 28 }}
                                />
                            }
                            title="Preview Client App"
                            description="See what your customers will experience"
                            onClick={goToClientApp}
                            primary
                            delay={100}
                        />
                        <ActionCard
                            icon={
                                <DashboardRoundedIcon
                                    sx={{ color: "white", fontSize: 28 }}
                                />
                            }
                            title="Set Up Admin App"
                            description="Manage bookings, services, and staff"
                            onClick={goToAdminInfo}
                            delay={200}
                        />
                    </Stack>
                </Stack>
            </Stack>
        </OnboardingShell>
    );
};

const ActionCard = ({
    icon,
    title,
    description,
    onClick,
    primary = false,
    delay = 0,
}: {
    icon: React.ReactNode;
    title: string;
    description: string;
    onClick: () => void;
    primary?: boolean;
    delay?: number;
}) => (
    <Box
        role="button"
        onClick={onClick}
        sx={{
            width: "90%",
            maxWidth: APP_MAX_WIDTH,
            cursor: "pointer",
            userSelect: "none",
            borderRadius: "20px",
            ...GLASS_SX,
            bgcolor: primary
                ? "rgba(255,255,255,0.22)"
                : "rgba(255,255,255,0.12)",
            p: 2,
            transition: "transform 0.15s ease, background 0.15s ease",
            "&:hover": {
                transform: "translateY(-2px)",
                bgcolor: "rgba(255,255,255,0.28)",
            },
            "@keyframes cardSlideUp": {
                from: { opacity: 0, transform: "translateY(16px)" },
                to: { opacity: 1, transform: "translateY(0)" },
            },
            animation: `cardSlideUp 0.4s ease-out ${delay}ms both`,
        }}
    >
        <Stack direction="row" spacing={2} alignItems="center">
            <Box
                sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "16px",
                    bgcolor: "rgba(255,255,255,0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "0 0 auto",
                }}
            >
                {icon}
            </Box>
            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: FONTS.display,
                        fontWeight: 600,
                        fontSize: "16px",
                        color: "white",
                        lineHeight: 1.2,
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: FONTS.body,
                        fontSize: "13px",
                        color: "rgba(255,255,255,0.65)",
                        lineHeight: 1.3,
                    }}
                >
                    {description}
                </Typography>
            </Stack>
            <ChevronRightRoundedIcon
                sx={{
                    color: "rgba(255,255,255,0.5)",
                    flex: "0 0 auto",
                }}
            />
        </Stack>
    </Box>
);

export default NewAnimatedCreation;
